import {
    TableColumnDefinition,
    createTableColumn,
    TableCellLayout,
    Tooltip,
    Button,
    useRestoreFocusTarget,
} from '@fluentui/react-components';
import {
    TextTRegular,
    TextUnderlineDoubleRegular,
    CalendarCheckmarkRegular,
    ChatRegular,
    ArrowDownload24Filled,
    ArrowDownload24Regular,
    bundleIcon,
    HistoryFilled,
    HistoryRegular,
} from '@fluentui/react-icons';
import { useMemo } from 'react';
import { formatDateUtc } from '../../../../i18n';
import { BroadcastMessageWithStatistic } from '../../../../model';
import { useTranslation } from 'react-i18next';
import BroadcastMessageStats from './BroadcastMessageStats/BroadcastMessageStats';

const ArrowDownloadIconBundle = bundleIcon(
    ArrowDownload24Filled,
    ArrowDownload24Regular
);

const HistoryIconBundle = bundleIcon(HistoryFilled, HistoryRegular);

export const broadcastMessageListColumnIds = {
    title: 'title',
    content: 'content',
    createdOnUtc: 'createdOnUtc',
    broadcastStats: 'stats/totalCount',
    actions: 'actions',
} as const;

export type BroadcastMessageColumnId =
    (typeof broadcastMessageListColumnIds)[keyof typeof broadcastMessageListColumnIds];

const excludedSimpleModeColumnIds: BroadcastMessageColumnId[] = [
    broadcastMessageListColumnIds.broadcastStats,
    broadcastMessageListColumnIds.actions,
];

export type BroadcastMessageReportPanelMode = 'full' | 'simple';

const ViewVersionsButton: React.FC<{ openDrawer: () => void }> = ({
    openDrawer,
}) => {
    const restoreFocusTargetAttributes = useRestoreFocusTarget();
    const { t } = useTranslation();

    return (
        <Tooltip
            content={t('Broadcast_ViewMessageVersions')}
            relationship="label"
            withArrow
        >
            <Button
                {...restoreFocusTargetAttributes}
                appearance="transparent"
                icon={<HistoryIconBundle />}
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    openDrawer();
                }}
            />
        </Tooltip>
    );
};

export const useBroadcastMessageReportPanelColumns = (
    mode: BroadcastMessageReportPanelMode,
    callbacks: {
        generateReport: (broadcastMessageId: string) => void;
        openDrawer: (messageId: string) => void;
    }
) => {
    const { generateReport, openDrawer } = callbacks;

    return useMemo(() => {
        let columns: TableColumnDefinition<BroadcastMessageWithStatistic>[] = [
            createTableColumn({
                columnId: broadcastMessageListColumnIds.title,
                renderHeaderCell: () => {
                    return (
                        <TableCellLayout media={<TextTRegular />}>
                            Title
                        </TableCellLayout>
                    );
                },
                renderCell: (item) => {
                    return <TableCellLayout>{item.title}</TableCellLayout>;
                },
            }),
            createTableColumn({
                columnId: broadcastMessageListColumnIds.content,
                renderHeaderCell: () => {
                    return (
                        <TableCellLayout media={<TextUnderlineDoubleRegular />}>
                            Description
                        </TableCellLayout>
                    );
                },
                renderCell: (item) => {
                    return <TableCellLayout>{item.content}</TableCellLayout>;
                },
            }),
            createTableColumn({
                columnId: broadcastMessageListColumnIds.createdOnUtc,
                renderHeaderCell: () => {
                    return (
                        <TableCellLayout media={<CalendarCheckmarkRegular />}>
                            Date/Time
                        </TableCellLayout>
                    );
                },
                renderCell: (item) => {
                    return (
                        <TableCellLayout>
                            {formatDateUtc(item.createdOnUtc, 'DD/MM/YY HH:mm')}
                        </TableCellLayout>
                    );
                },
            }),
            createTableColumn({
                columnId: broadcastMessageListColumnIds.broadcastStats,
                renderHeaderCell: () => {
                    return (
                        <TableCellLayout media={<ChatRegular />}>
                            Flights Broadcast
                        </TableCellLayout>
                    );
                },
                renderCell: (item) => {
                    return <BroadcastMessageStats data={item.stats} />;
                },
            }),
            createTableColumn({
                columnId: broadcastMessageListColumnIds.actions,
                renderHeaderCell: () => {
                    return <></>;
                },
                renderCell: (item) => {
                    return (
                        <TableCellLayout>
                            <Tooltip
                                content={'Generate report'}
                                relationship="label"
                                withArrow
                            >
                                <Button
                                    appearance="transparent"
                                    icon={<ArrowDownloadIconBundle />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        generateReport(item.id);
                                    }}
                                />
                            </Tooltip>
                            {item.previousVersionCount > 0 && (
                                <ViewVersionsButton
                                    openDrawer={() => openDrawer(item.id)}
                                />
                            )}
                        </TableCellLayout>
                    );
                },
            }),
        ];

        if (mode === 'simple') {
            columns = columns.filter(
                (x) =>
                    !excludedSimpleModeColumnIds.includes(
                        x.columnId as BroadcastMessageColumnId
                    )
            );
        }

        return columns;
    }, [mode, generateReport, openDrawer]);
};
